/**
 * T-26: Install Prompt Component
 * 
 * Provides a user-friendly interface for installing the PWA
 */

import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone, Monitor, Zap } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { toggleInstallPrompt } from '../store/slices/preferencesSlice';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface InstallPromptProps {
  onInstall?: () => void;
  onDismiss?: () => void;
  className?: string;
}

export const InstallPrompt: React.FC<InstallPromptProps> = ({
  onInstall,
  onDismiss,
  className = ''
}) => {
  const dispatch = useDispatch();
  const showInstallPrompt = useSelector((state: RootState) => state.preferences.showInstallPrompt);
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalling, setIsInstalling] = useState(false);
  const [canInstall, setCanInstall] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    // Check if prompt is already available
    if (window.deferredPrompt && showInstallPrompt) {
      setDeferredPrompt(window.deferredPrompt);
      setCanInstall(true);
      setTimeout(() => setShowPrompt(true), 2000);
    }

    // Listen for install available event from main.tsx
    const handleInstallAvailable = (e: CustomEvent) => {
      console.log('[InstallPrompt] Install available:', e.detail);
      if (showInstallPrompt) {
        setDeferredPrompt(e.detail);
        setCanInstall(true);
        setTimeout(() => setShowPrompt(true), 2000);
      }
    };

    // Listen for app installation
    const handleAppInstalled = () => {
      console.log('[InstallPrompt] App was installed');
      setShowPrompt(false);
      setDeferredPrompt(null);
      setCanInstall(false);
      onInstall?.();
    };

    window.addEventListener('pwa-install-available', handleInstallAvailable as EventListener);
    window.addEventListener('pwa-app-installed', handleAppInstalled as EventListener);

    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setCanInstall(false);
    }

    return () => {
      window.removeEventListener('pwa-install-available', handleInstallAvailable as EventListener);
      window.removeEventListener('pwa-app-installed', handleAppInstalled as EventListener);
    };
  }, [onInstall]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    setIsInstalling(true);

    try {
      // Show the install prompt
      await deferredPrompt.prompt();
      
      // Wait for the user's response
      const result = await deferredPrompt.userChoice;
      
      if (result.outcome === 'accepted') {
        console.log('[PWA] User accepted the install prompt');
        setShowPrompt(false);
      } else {
        console.log('[PWA] User dismissed the install prompt');
      }
    } catch (error) {
      console.error('[PWA] Install prompt failed:', error);
    } finally {
      setIsInstalling(false);
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    onDismiss?.();
    
    // If "don't show again" is checked, update the preference
    if (dontShowAgain) {
      dispatch(toggleInstallPrompt());
    }
    
    // Don't show again for this session
    setCanInstall(false);
  };

  const handleLater = () => {
    setShowPrompt(false);
    
    // Show again after 24 hours
    setTimeout(() => {
      if (canInstall) {
        setShowPrompt(true);
      }
    }, 24 * 60 * 60 * 1000);
  };

  if (!showPrompt || !canInstall) {
    return null;
  }

  return (
    <div className={`fixed bottom-4 right-4 max-w-sm z-50 ${className}`}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-6 relative">
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          aria-label="Dismiss install prompt"
        >
          <X size={16} />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-blue-100 dark:bg-blue-900 p-2 rounded-lg">
            <Download className="text-blue-600 dark:text-blue-400" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
              Install Number Line
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Get the full app experience
            </p>
          </div>
        </div>

        {/* Benefits */}
        <div className="space-y-3 mb-6">
          <div className="flex items-center gap-3 text-sm">
            <Zap className="text-green-600 dark:text-green-400" size={16} />
            <span className="text-gray-700 dark:text-gray-300">Faster loading & offline access</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Smartphone className="text-blue-600 dark:text-blue-400" size={16} />
            <span className="text-gray-700 dark:text-gray-300">Add to home screen</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Monitor className="text-purple-600 dark:text-purple-400" size={16} />
            <span className="text-gray-700 dark:text-gray-300">Fullscreen experience</span>
          </div>
        </div>

        {/* Don't show again checkbox */}
        <div className="flex items-center gap-2 mb-4">
          <input
            type="checkbox"
            id="dont-show-again"
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
          />
          <label
            htmlFor="dont-show-again"
            className="text-sm text-gray-600 dark:text-gray-400 cursor-pointer"
          >
            Don't show this again
          </label>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={handleInstallClick}
            disabled={isInstalling}
            className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
          >
            {isInstalling ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Installing...
              </>
            ) : (
              <>
                <Download size={16} />
                Install
              </>
            )}
          </button>
          <button
            onClick={handleLater}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          >
            Later
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Manual install button for settings/preferences
 */
export const InstallButton: React.FC<{
  variant?: 'primary' | 'secondary';
  size?: 'sm' | 'md' | 'lg';
  children?: React.ReactNode;
}> = ({ 
  variant = 'primary', 
  size = 'md',
  children
}) => {
  const [canInstall, setCanInstall] = useState(!!window.deferredPrompt);
  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    const handleInstallAvailable = () => {
      setCanInstall(true);
    };

    const handleAppInstalled = () => {
      setCanInstall(false);
    };

    window.addEventListener('pwa-install-available', handleInstallAvailable);
    window.addEventListener('pwa-app-installed', handleAppInstalled);

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setCanInstall(false);
    }

    return () => {
      window.removeEventListener('pwa-install-available', handleInstallAvailable);
      window.removeEventListener('pwa-app-installed', handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!window.deferredPrompt) return;

    setIsInstalling(true);
    try {
      await window.deferredPrompt.prompt();
      const result = await window.deferredPrompt.userChoice;
      
      if (result.outcome === 'accepted') {
        window.deferredPrompt = null;
        setCanInstall(false);
      }
    } catch (error) {
      console.error('[PWA] Install failed:', error);
    } finally {
      setIsInstalling(false);
    }
  };

  if (!canInstall) {
    return null;
  }

  const baseClasses = "flex items-center justify-center gap-2 font-medium rounded-lg transition-colors disabled:opacity-50";
  
  const variantClasses = {
    primary: "bg-blue-600 hover:bg-blue-700 text-white",
    secondary: "bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100"
  };

  const sizeClasses = {
    sm: "px-3 py-2 text-sm",
    md: "px-4 py-2 text-base",
    lg: "px-6 py-3 text-lg"
  };

  return (
    <button
      onClick={handleInstall}
      disabled={isInstalling}
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]}`}
    >
      {isInstalling ? (
        <>
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          Installing...
        </>
      ) : (
        <>
          <Download size={16} />
          {children || 'Install App'}
        </>
      )}
    </button>
  );
};